// src/pages/BlogList.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { blogPosts, getAllCategories, getAllTags } from '../data/blogData';
import { Calendar, Clock, Tag, Search, ArrowRight } from 'lucide-react';
import '../styles/BlogList.css';

const BlogList = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['All', ...getAllCategories()];
  const allTags = getAllTags();

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesTag = !selectedTag || post.tags.includes(selectedTag);
    const matchesSearch = !searchQuery ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCategory && matchesTag && matchesSearch;
  });

  // Get featured posts
  const featuredPosts = blogPosts.filter(post => post.featured).slice(0, 2);

  return (
    <div className="blog-list-page">
      <SEO
        title="Insights & Articles | EDSY Tech"
        description="Read expert articles on enterprise SaaS architecture, governance, cloud operations, and leadership strategy from EDSY Tech."
        canonicalPath="/blog"
        keywords="enterprise SaaS articles, architecture insights, cloud strategy, governance, leadership, technology blog"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-container">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">Leadership & Technical Insights</h1>
            <p className="blog-hero-subtitle">
              Expert perspectives on enterprise architecture, governance, and technology leadership
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="blog-filters-section">
        <div className="blog-filters-container">
          {/* Search Bar */}
          <div className="search-bar">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Category Filter */}
          <div className="category-filter">
            <h3 className="filter-title">Categories</h3>
            <div className="category-pills">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setSelectedTag(null);
                  }}
                  className={`category-pill ${selectedCategory === category ? 'category-pill-active' : ''}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Tag Filter */}
          <div className="tag-filter">
            <h3 className="filter-title">Tags</h3>
            <div className="tag-pills">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  className={`tag-pill ${selectedTag === tag ? 'tag-pill-active' : ''}`}
                >
                  <Tag className="tag-icon" />
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {!searchQuery && !selectedTag && selectedCategory === 'All' && (
        <section className="featured-posts-section">
          <div className="featured-posts-container">
            <h2 className="featured-title">Featured Articles</h2>
            <div className="featured-posts-grid">
              {featuredPosts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="featured-post-card"
                >
                  <div className="featured-post-image" style={{ backgroundImage: `url(${post.image})` }}>
                    <div className="featured-badge">Featured</div>
                  </div>
                  <div className="featured-post-content">
                    <div className="featured-post-meta">
                      <span className="post-category">{post.category}</span>
                      <span className="post-date">
                        <Calendar className="meta-icon" />
                        {new Date(post.publishedDate).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <h3 className="featured-post-title">{post.title}</h3>
                    <p className="featured-post-excerpt">{post.excerpt}</p>
                    <div className="featured-post-footer">
                      {/* <div className="post-author">
                        <img src={post.author.avatar} alt={post.author.name} className="author-avatar" />
                        <div className="author-info">
                          <div className="author-name">{post.author.name}</div>
                          <div className="author-role">{post.author.role}</div>
                        </div>
                      </div> */}
                      <div className="post-read-time">
                        <Clock className="meta-icon" />
                        {post.readTime}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section className="all-posts-section">
        <div className="all-posts-container">
          <div className="posts-header">
            <h2 className="posts-title">
              {selectedCategory !== 'All' ? `${selectedCategory} Articles` : 'All Articles'}
              {selectedTag && ` tagged with "${selectedTag}"`}
            </h2>
            <p className="posts-count">{filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}</p>
          </div>

          {filteredPosts.length === 0 ? (
            <div className="no-results">
              <p>No articles found matching your criteria.</p>
              <button
                onClick={() => {
                  setSelectedCategory('All');
                  setSelectedTag(null);
                  setSearchQuery('');
                }}
                className="reset-filters-button"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="posts-grid">
              {filteredPosts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="post-card"
                >
                  <div className="post-image" style={{ backgroundImage: `url(${post.image})` }}>
                    {post.featured && <div className="featured-small-badge">Featured</div>}
                  </div>
                  <div className="post-card-content">
                    <div className="post-card-meta">
                      <span className="post-category-small">{post.category}</span>
                      <span className="post-read-time-small">
                        <Clock className="meta-icon-small" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="post-card-title">{post.title}</h3>
                    <p className="post-card-excerpt">{post.excerpt}</p>
                    <div className="post-card-tags">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="post-tag-small">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="post-card-footer">
                      {/* <div className="post-author-small">
                        <img src={post.author.avatar} alt={post.author.name} className="author-avatar-small" />
                        <span className="author-name-small">{post.author.name}</span>
                      </div>
                      <div className="post-date-small">
                        {new Date(post.publishedDate).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                     */
                      }
                    </div>
                  </div>
                  <div className="post-card-hover">
                    <span>Read Article</span>
                    <ArrowRight className="hover-arrow" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="newsletter-section">
        <div className="newsletter-container">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Stay Updated</h2>
            <p className="newsletter-text">
              Get the latest insights on enterprise architecture, governance, and technology leadership delivered to your inbox.
            </p>
            <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Enter your email"
                className="newsletter-input"
                required
              />
              <button type="submit" className="newsletter-button">
                Subscribe
                <ArrowRight className="button-arrow" />
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogList;